import { useState, useEffect, useRef } from "react";

/* ─── LOGO SVG INLINE ─────────────────────────────────────────── */
const Logo = ({ size = 36 }) => (
  <svg width={size * 3.25} height={size} viewBox="0 0 520 160" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(50,40)">
      <circle cx="20" cy="40" r="14" fill="#22C55E" />
      <circle cx="70" cy="15" r="14" fill="#3B82F6" />
      <circle cx="70" cy="65" r="14" fill="#F59E0B" />
      <line x1="32" y1="40" x2="56" y2="20" stroke="#94A3B8" strokeWidth="4" />
      <line x1="32" y1="40" x2="56" y2="60" stroke="#94A3B8" strokeWidth="4" />
    </g>
    <text x="160" y="95" fontFamily="'Cormorant Garamond', Georgia, serif" fontSize="62" fontWeight="700" fill="#F8FAFC">
      spaytex
    </text>
  </svg>
);

/* ─── HOOK: Intersection Observer ─────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ─── FADE-IN WRAPPER ──────────────────────────────────────────── */
function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.75s ${delay}s ease, transform 0.75s ${delay}s ease`,
      }}
    >
      {children}
    </div>
  );
}

/* ─── DATA ─────────────────────────────────────────────────────── */
const BRANDS = [
  {
    emoji: "🛍️",
    name: "StyleSA",
    tag: "Fashion Retail",
    desc: "Curated contemporary fashion for the modern South African, delivered to your door.",
    color: "#22C55E",
    bg: "rgba(34,197,94,0.08)",
    border: "rgba(34,197,94,0.25)",
  },
  {
    emoji: "💻",
    name: "TechDirect",
    tag: "Electronics",
    desc: "Latest gadgets, laptops and smart home devices at competitive South African pricing.",
    color: "#3B82F6",
    bg: "rgba(59,130,246,0.08)",
    border: "rgba(59,130,246,0.25)",
  },
  {
    emoji: "🏠",
    name: "HomeNest",
    tag: "Home & Living",
    desc: "Premium homeware, décor and furniture for every South African lifestyle and budget.",
    color: "#F59E0B",
    bg: "rgba(245,158,11,0.08)",
    border: "rgba(245,158,11,0.25)",
  },
  {
    emoji: "🌿",
    name: "WellSA",
    tag: "Health & Wellness",
    desc: "Health supplements, beauty and wellness products sourced and verified for SA consumers.",
    color: "#22C55E",
    bg: "rgba(34,197,94,0.08)",
    border: "rgba(34,197,94,0.25)",
  },
  {
    emoji: "📦",
    name: "QuickBox",
    tag: "Logistics Services",
    desc: "Last-mile delivery and fulfilment solutions powering SA's growing e-commerce ecosystem.",
    color: "#3B82F6",
    bg: "rgba(59,130,246,0.08)",
    border: "rgba(59,130,246,0.25)",
  },
  {
    emoji: "🎓",
    name: "SkillUp",
    tag: "Online Learning",
    desc: "Affordable online courses and certifications tailored to South African career pathways.",
    color: "#F59E0B",
    bg: "rgba(245,158,11,0.08)",
    border: "rgba(245,158,11,0.25)",
  },
];

const VALUES = [
  { icon: "🇿🇦", title: "South African First", desc: "Every brand under our portfolio is built specifically for the South African market and consumer." },
  { icon: "⚡", title: "Digital-Native", desc: "We operate exclusively online, passing savings and convenience directly to our customers." },
  { icon: "🤝", title: "Transformation", desc: "Proudly B-BBEE Level 1, committed to economic inclusion and empowerment." },
  { icon: "🌱", title: "Sustainable Growth", desc: "Building brands responsibly with long-term impact for communities and the environment." },
];

/* ─── NETWORK ANIMATION CANVAS ────────────────────────────────── */
function NetworkCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let W, H, nodes, raf;
    const COLORS = ["#22C55E", "#3B82F6", "#F59E0B"];

    function resize() {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    }

    function init() {
      resize();
      nodes = Array.from({ length: 28 }, (_, i) => ({
        x: Math.random() * W,
        y: Math.random() * H,
        r: 3 + Math.random() * 4,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        color: COLORS[i % 3],
      }));
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      // draw edges
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(148,163,184,${0.18 * (1 - dist / 130)})`;
            ctx.lineWidth = 1;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      // draw nodes
      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = n.color + "99";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r - 1.5, 0, Math.PI * 2);
        ctx.fillStyle = n.color;
        ctx.fill();

        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
      });
      raf = requestAnimationFrame(draw);
    }

    init();
    draw();
    window.addEventListener("resize", init);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", init); };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute", inset: 0, width: "100%", height: "100%",
        pointerEvents: "none",
      }}
    />
  );
}

/* ─── NAV ──────────────────────────────────────────────────────── */
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = ["About", "Brands", "Values", "Contact"];

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 999,
      padding: "0 5vw",
      height: 68,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: scrolled ? "rgba(15,23,42,0.92)" : "transparent",
      backdropFilter: scrolled ? "blur(16px)" : "none",
      borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "none",
      transition: "background 0.4s, backdrop-filter 0.4s, border 0.4s",
    }}>
      <Logo size={84} />

      {/* Desktop links */}
      <div style={{ display: "flex", gap: 36, alignItems: "center" }}>
        {links.map((l) => (
          <a
            key={l}
            href={`#${l.toLowerCase()}`}
            style={{
              color: "rgba(248,250,252,0.7)", fontSize: 14, fontWeight: 500,
              textDecoration: "none", letterSpacing: "0.03em",
              transition: "color 0.2s",
              fontFamily: "'DM Sans', sans-serif",
            }}
            onMouseEnter={e => e.target.style.color = "#22C55E"}
            onMouseLeave={e => e.target.style.color = "rgba(248,250,252,0.7)"}
          >
            {l}
          </a>
        ))}
        <a
          href="mailto:info@spaytex.co.za"
          style={{
            background: "#22C55E", color: "#0F172A",
            padding: "9px 22px", borderRadius: 6,
            fontSize: 13, fontWeight: 700, textDecoration: "none",
            letterSpacing: "0.04em", fontFamily: "'DM Sans', sans-serif",
            transition: "background 0.2s",
          }}
          onMouseEnter={e => e.target.style.background = "#16A34A"}
          onMouseLeave={e => e.target.style.background = "#22C55E"}
        >
          Get In Touch
        </a>
      </div>
    </nav>
  );
}

/* ─── HERO ─────────────────────────────────────────────────────── */
function Hero() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  const fade = (delay) => ({
    opacity: loaded ? 1 : 0,
    transform: loaded ? "translateY(0)" : "translateY(28px)",
    transition: `opacity 0.85s ${delay}s ease, transform 0.85s ${delay}s ease`,
  });

  return (
    <section id="home" style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0F172A 0%, #0F1F3D 50%, #0F172A 100%)",
      position: "relative", overflow: "hidden",
      display: "flex", flexDirection: "column", justifyContent: "center",
      padding: "120px 5vw 80px",
    }}>
      <NetworkCanvas />

      {/* Gradient accents */}
      <div style={{
        position: "absolute", top: "10%", right: "5%",
        width: 480, height: 480, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(34,197,94,0.12) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: "5%", left: "10%",
        width: 360, height: 360, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />

      <div style={{ position: "relative", zIndex: 2, maxWidth: 900 }}>
        {/* Badge */}
        <div style={{
          ...fade(0.1),
          display: "inline-flex", alignItems: "center", gap: 10,
          background: "rgba(34,197,94,0.12)",
          border: "1px solid rgba(34,197,94,0.3)",
          borderRadius: 100, padding: "7px 18px",
          marginBottom: 36,
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%", background: "#22C55E",
            animation: "pulse 2s infinite",
          }} />
          <span style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
            textTransform: "uppercase", color: "#4ADE80",
          }}>
            South Africa's Specialty Retail Group
          </span>
        </div>

        {/* Headline */}
        <h1 style={{
          ...fade(0.25),
          fontFamily: "'Cormorant Garamond', 'Georgia', serif",
          fontSize: "clamp(48px, 8vw, 100px)",
          fontWeight: 700,
          lineHeight: 1.02,
          letterSpacing: "-2px",
          color: "#F8FAFC",
          margin: 0,
        }}>
          Connecting{" "}
          <span style={{ color: "#22C55E" }}>South African</span>
          <br />consumers to the{" "}
          <span style={{
            background: "linear-gradient(90deg, #3B82F6, #F59E0B)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>
            brands
          </span>{" "}
          they love.
        </h1>

        <p style={{
          ...fade(0.4),
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "clamp(16px, 2vw, 20px)",
          fontWeight: 300,
          color: "rgba(248,250,252,0.6)",
          maxWidth: 560, lineHeight: 1.75,
          margin: "32px 0 0",
        }}>
          Spaytex owns and operates a growing portfolio of specialty online retail
          and service brands, purpose-built for the South African consumer.
        </p>

        <div style={{ ...fade(0.55), display: "flex", gap: 16, marginTop: 48, flexWrap: "wrap" }}>
          <a href="#brands" style={{
            background: "#22C55E", color: "#0F172A",
            padding: "15px 36px", borderRadius: 8,
            fontSize: 15, fontWeight: 700,
            textDecoration: "none",
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: "0.02em",
            transition: "background 0.2s, transform 0.15s",
            display: "inline-block",
          }}
          onMouseEnter={e => { e.target.style.background = "#16A34A"; e.target.style.transform = "translateY(-2px)"; }}
          onMouseLeave={e => { e.target.style.background = "#22C55E"; e.target.style.transform = "translateY(0)"; }}
          >
            Explore Our Brands
          </a>
          <a href="#about" style={{
            border: "1.5px solid rgba(248,250,252,0.2)",
            color: "#F8FAFC",
            padding: "15px 36px", borderRadius: 8,
            fontSize: 15, fontWeight: 600,
            textDecoration: "none",
            fontFamily: "'DM Sans', sans-serif",
            transition: "border-color 0.2s",
            display: "inline-block",
          }}
          onMouseEnter={e => e.target.style.borderColor = "rgba(248,250,252,0.5)"}
          onMouseLeave={e => e.target.style.borderColor = "rgba(248,250,252,0.2)"}
          >
            Learn About Us
          </a>
        </div>

        {/* Stats */}
        <div style={{
          ...fade(0.7),
          display: "flex", gap: 0, marginTop: 80,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          paddingTop: 40,
          flexWrap: "wrap",
        }}>
          {[
            { num: "6+", label: "Brands in portfolio" },
            { num: "B-BBEE", label: "Level 1 certified" },
            { num: "100%", label: "Digital-first operations" },
            { num: "2025", label: "Founded in SA" },
          ].map((s, i) => (
            <div key={i} style={{
              paddingRight: 48, marginRight: 48,
              borderRight: i < 3 ? "1px solid rgba(255,255,255,0.08)" : "none",
              marginBottom: 20,
            }}>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 36, fontWeight: 700, color: "#F8FAFC", lineHeight: 1,
              }}>{s.num}</div>
              <div style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12, fontWeight: 500, color: "rgba(248,250,252,0.4)",
                marginTop: 6, textTransform: "uppercase", letterSpacing: "0.06em",
              }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%,100% { opacity:1; transform:scale(1); }
          50% { opacity:0.4; transform:scale(1.5); }
        }
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');
      `}</style>
    </section>
  );
}

/* ─── ABOUT ─────────────────────────────────────────────────────── */
function About() {
  return (
    <section id="about" style={{ background: "#F8FAFC", padding: "100px 5vw" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>

          {/* Left: Text */}
          <div>
            <Reveal>
              <div style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
                textTransform: "uppercase", color: "#22C55E", marginBottom: 16,
              }}>About Spaytex</div>
              <h2 style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(34px, 4vw, 56px)", fontWeight: 700,
                lineHeight: 1.1, letterSpacing: "-1px",
                color: "#0F172A", margin: "0 0 24px",
              }}>
                A holding group built for South Africa's digital future.
              </h2>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16, fontWeight: 300, lineHeight: 1.85,
                color: "#475569", margin: "0 0 20px",
              }}>
                Spaytex Pty Ltd is a South African registered holding company that incubates,
                owns and scales specialty online retail and service brands. Every brand in our
                portfolio is designed from the ground up to serve the unique needs of South
                African consumers — accessible, affordable, and reliable.
              </p>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16, fontWeight: 300, lineHeight: 1.85,
                color: "#475569", margin: 0,
              }}>
                Led by Director S F Mokoena, we are committed to economic transformation
                and building a proudly South African digital commerce ecosystem that creates
                opportunity for all.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 40 }}>
                {[
                  { icon: "📋", label: "Company Registration", val: "2025 / 698671 / 07" },
                  { icon: "🏢", label: "Director", val: "S F Mokoena" },
                  { icon: "🌐", label: "Website", val: "spaytex.co.za" },
                  { icon: "📧", label: "Email", val: "info@spaytex.co.za" },
                ].map(({ icon, label, val }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: 10,
                      background: "rgba(15,23,42,0.06)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 18, flexShrink: 0,
                    }}>{icon}</div>
                    <div>
                      <div style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                        textTransform: "uppercase", color: "#94A3B8",
                      }}>{label}</div>
                      <div style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 14, fontWeight: 500, color: "#1E293B",
                      }}>{val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right: Cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <Reveal delay={0.1}>
              <div style={{
                background: "#0F172A", borderRadius: 20, padding: 40,
                position: "relative", overflow: "hidden",
              }}>
                <div style={{
                  position: "absolute", top: -40, right: -40,
                  width: 180, height: 180, borderRadius: "50%",
                  background: "rgba(34,197,94,0.12)",
                }} />
                <div style={{ position: "relative", zIndex: 1 }}>
                  <div style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 48, fontWeight: 700, color: "#22C55E", lineHeight: 1,
                  }}>B-BBEE</div>
                  <div style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 22, fontWeight: 600, color: "#F8FAFC", marginTop: 8,
                  }}>Level 1 Contributor</div>
                  <p style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 14, fontWeight: 300,
                    color: "rgba(248,250,252,0.55)", lineHeight: 1.7,
                    marginTop: 14,
                  }}>
                    Certificate #9442945369 — Committed to Broad-Based Black
                    Economic Empowerment and meaningful transformation.
                  </p>
                  <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
                    {["Transformation", "Level 1", "Verified"].map(tag => (
                      <span key={tag} style={{
                        background: "rgba(34,197,94,0.12)",
                        border: "1px solid rgba(34,197,94,0.25)",
                        color: "#4ADE80",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 11, fontWeight: 600, letterSpacing: "0.05em",
                        padding: "5px 13px", borderRadius: 100,
                      }}>{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16,
              }}>
                {[
                  { color: "#22C55E", bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.2)", icon: "🛒", label: "Online Retail" },
                  { color: "#3B82F6", bg: "rgba(59,130,246,0.08)", border: "rgba(59,130,246,0.2)", icon: "⚡", label: "Digital Services" },
                  { color: "#F59E0B", bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.2)", icon: "🌍", label: "SA-Focused" },
                  { color: "#22C55E", bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.2)", icon: "🤝", label: "BEE Compliant" },
                ].map(({ color, bg, border, icon, label }) => (
                  <div key={label} style={{
                    background: bg, border: `1px solid ${border}`,
                    borderRadius: 14, padding: 22, textAlign: "center",
                  }}>
                    <div style={{ fontSize: 26, marginBottom: 8 }}>{icon}</div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 13, fontWeight: 600, color: color,
                    }}>{label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── BRANDS ─────────────────────────────────────────────────────── */
function Brands() {
  return (
    <section id="brands" style={{ background: "#0F172A", padding: "100px 5vw" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Reveal>
          <div style={{ marginBottom: 64 }}>
            <div style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
              textTransform: "uppercase", color: "#3B82F6", marginBottom: 16,
            }}>Our Portfolio</div>
            <h2 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(34px, 4vw, 56px)", fontWeight: 700,
              lineHeight: 1.1, letterSpacing: "-1px",
              color: "#F8FAFC", margin: "0 0 16px",
              maxWidth: 600,
            }}>
              Specialty brands built for every South African need.
            </h2>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16, fontWeight: 300, lineHeight: 1.75,
              color: "rgba(248,250,252,0.55)", maxWidth: 500,
            }}>
              Each brand is independently operated with its own identity, team
              and market focus — all united under the Spaytex umbrella.
            </p>
          </div>
        </Reveal>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 24,
        }}>
          {BRANDS.map((brand, i) => (
            <Reveal key={brand.name} delay={i * 0.08}>
              <div
                style={{
                  background: brand.bg,
                  border: `1px solid ${brand.border}`,
                  borderRadius: 18, padding: 36,
                  position: "relative", overflow: "hidden",
                  cursor: "default",
                  transition: "transform 0.25s, box-shadow 0.25s",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = "translateY(-6px)";
                  e.currentTarget.style.boxShadow = `0 20px 60px rgba(0,0,0,0.3)`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Top accent line */}
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 3,
                  background: brand.color,
                }} />

                <div style={{ fontSize: 30, marginBottom: 18 }}>{brand.emoji}</div>

                <div style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
                  textTransform: "uppercase", color: brand.color,
                  marginBottom: 8,
                }}>{brand.tag}</div>

                <h3 style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 26, fontWeight: 700, color: "#F8FAFC",
                  margin: "0 0 12px",
                }}>{brand.name}</h3>

                <p style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14, fontWeight: 300, lineHeight: 1.75,
                  color: "rgba(248,250,252,0.55)", margin: 0,
                }}>{brand.desc}</p>

                <div style={{
                  marginTop: 24, display: "inline-flex", alignItems: "center",
                  gap: 6, color: brand.color,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13, fontWeight: 600,
                  cursor: "pointer",
                }}>
                  Learn more <span style={{ fontSize: 16 }}>→</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── VALUES ─────────────────────────────────────────────────────── */
function Values() {
  return (
    <section id="values" style={{ background: "#F1F5F9", padding: "100px 5vw" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: 72 }}>
            <div style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
              textTransform: "uppercase", color: "#F59E0B", marginBottom: 16,
            }}>What We Stand For</div>
            <h2 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(34px, 4vw, 56px)", fontWeight: 700,
              lineHeight: 1.1, letterSpacing: "-1px",
              color: "#0F172A", margin: "0 auto 16px", maxWidth: 560,
            }}>
              Our core values guide every brand we build.
            </h2>
          </div>
        </Reveal>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 24,
        }}>
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div style={{
                background: "#fff", borderRadius: 18, padding: 36,
                border: "1px solid #E2E8F0",
                transition: "box-shadow 0.25s, transform 0.2s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.08)";
                e.currentTarget.style.transform = "translateY(-4px)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
              }}
              >
                <div style={{ fontSize: 36, marginBottom: 20 }}>{v.icon}</div>
                <h3 style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 22, fontWeight: 700, color: "#0F172A",
                  margin: "0 0 12px",
                }}>{v.title}</h3>
                <p style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14, fontWeight: 300, lineHeight: 1.75,
                  color: "#64748B", margin: 0,
                }}>{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CONTACT ─────────────────────────────────────────────────────── */
function Contact() {
  return (
    <section id="contact" style={{ background: "#0F172A", padding: "100px 5vw" }}>
      <div style={{
        maxWidth: 860, margin: "0 auto", textAlign: "center",
      }}>
        <Reveal>
          <div style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
            textTransform: "uppercase", color: "#22C55E", marginBottom: 16,
          }}>Get In Touch</div>
          <h2 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(36px, 5vw, 66px)", fontWeight: 700,
            lineHeight: 1.08, letterSpacing: "-1.5px",
            color: "#F8FAFC", margin: "0 auto 24px",
          }}>
            Partner with us or learn more about our brands.
          </h2>
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 16, fontWeight: 300, lineHeight: 1.8,
            color: "rgba(248,250,252,0.55)", maxWidth: 480, margin: "0 auto 48px",
          }}>
            Whether you're a prospective brand partner, supplier, investor or just
            curious — we'd love to hear from you.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div style={{ display: "flex", gap: 20, justifyContent: "center", flexWrap: "wrap" }}>
            <a
              href="mailto:info@spaytex.co.za"
              style={{
                background: "#22C55E", color: "#0F172A",
                padding: "16px 40px", borderRadius: 8,
                fontSize: 15, fontWeight: 700,
                textDecoration: "none",
                fontFamily: "'DM Sans', sans-serif",
                display: "inline-block",
                transition: "background 0.2s, transform 0.15s",
              }}
              onMouseEnter={e => { e.target.style.background = "#16A34A"; e.target.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.target.style.background = "#22C55E"; e.target.style.transform = "translateY(0)"; }}
            >
              📧 info@spaytex.co.za
            </a>
            <a
              href="https://spaytex.co.za"
              style={{
                border: "1.5px solid rgba(248,250,252,0.2)",
                color: "#F8FAFC",
                padding: "16px 40px", borderRadius: 8,
                fontSize: 15, fontWeight: 600,
                textDecoration: "none",
                fontFamily: "'DM Sans', sans-serif",
                display: "inline-block",
                transition: "border-color 0.2s",
              }}
              onMouseEnter={e => e.target.style.borderColor = "rgba(248,250,252,0.5)"}
              onMouseLeave={e => e.target.style.borderColor = "rgba(248,250,252,0.2)"}
            >
              🌐 spaytex.co.za
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── FOOTER ─────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer style={{
      background: "#080F1E",
      padding: "48px 5vw",
      borderTop: "1px solid rgba(255,255,255,0.06)",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 32 }}>
          <div>
            <Logo size={88} />
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13, fontWeight: 300,
              color: "rgba(248,250,252,0.35)",
              marginTop: 16, maxWidth: 320, lineHeight: 1.7,
            }}>
              South Africa's specialty online retail and services group.
              <br />Building brands that serve every South African.
            </p>
          </div>


        </div>

        {/* Tricolour rule */}
        <div style={{ display: "flex", height: 3, borderRadius: 2, overflow: "hidden", margin: "36px 0 24px" }}>
          <div style={{ flex: 1, background: "#22C55E" }} />
          <div style={{ flex: 1, background: "#3B82F6" }} />
          <div style={{ flex: 1, background: "#F59E0B" }} />
        </div>

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 12,
        }}>
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 12, color: "rgba(248,250,252,0.25)", margin: 0,
          }}>
            © {new Date().getFullYear()} Spaytex Pty Ltd. All rights reserved. Registered in South Africa.
          </p>
          <div style={{ display: "flex", gap: 24 }}>
            {["Privacy Policy", "Terms of Use", "Contact"].map(l => (
              <a key={l} href="#" style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12, color: "rgba(248,250,252,0.25)",
                textDecoration: "none", transition: "color 0.2s",
              }}
              onMouseEnter={e => e.target.style.color = "#22C55E"}
              onMouseLeave={e => e.target.style.color = "rgba(248,250,252,0.25)"}
              >{l}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── ROOT APP ─────────────────────────────────────────────────────── */
// NOTE: For favicon, copy logo_image.png into your project's /public folder
// and rename it favicon.png. The favicon tag in public/index.html will
// automatically pick it up via: <link rel="icon" href="%PUBLIC_URL%/favicon.png" />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";

export default function App() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700;900&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'DM Sans', sans-serif; overflow-x: hidden; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #0F172A; }
        ::-webkit-scrollbar-thumb { background: #334155; border-radius: 3px; }
      `}</style>
      <Nav />
      <Hero />
      <About />
      <Brands />
      <Values />
      <Contact />
      <Footer />
    </>
  );
}
